import axios from "axios";
import { HttpClient } from "./http/httpclient.service";
import { ApiRoutes } from "./http/routes";
import { IProduct, IProductAggregate } from "../types/Product";
import { IAddCartItem } from "../pages/Product";
import { CartStatus, ICart } from "../types/Cart";
import { Servers } from "./http/server";
import { SnackyPop } from "../components/Snackbar";
import { ICartItem } from "../types/Cart";

const API_BASE_URL = Servers.API; // Replace with your API URL

// Create a reusable axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// API Service Methods
const ProductService = {
  // Fetch all categories
  fetchCategories: async () => {
    try {
      const response = await api.get("/categories");
      return response.data;
    } catch (error) {
      console.error("Error fetching categories:", error);
      throw error;
    }
  },

  // Fetch a single product by ID
  fetchProductDetails: async (productId: string): Promise<IProductAggregate> => {
    try {

      let response = await HttpClient.get<IProductAggregate>({
        route: ApiRoutes.GetShopProductBySlug,
        uriParams: { slug: productId }
      });

      let data = response.data as IProductAggregate;
      return data;
    } catch (error) {
      console.error(`Error fetching product with ID ${productId}:`, error);
      throw error;
    }
  },

  searchProducts: async (): Promise<IProduct[]> => {
    try {
      let params = {
        name: "searchtext",
        offset: 2,
        count: 10,
      };
      let response = await HttpClient.get<IProduct[]>({
        route: ApiRoutes.SearchProducts,
        uriParams: { params }
      })
      return (response.data || []) as IProduct[];
    } catch (error) {
      console.error(`Error searching products`, error);
      throw error;
    }
  },

  // Fetch all products


  fetchAllProducts: async (pageNumber: number = 0, collections: string = "",filters:string=""): Promise<IProduct[]> => {
    try {
      const response = await HttpClient.get<IProduct[]>({
        route: ApiRoutes.GetProducts,
        uriParams: { pageNumber, collections,filters }
      });
      return (response.data || []) as IProduct[];
    } catch (error) {
      console.error("Error fetching all products:", error);
      throw error;
    }
  },
  fetchTopSelling: async (): Promise<IProduct[]> => {
    try {
      const response = await HttpClient.get<IProduct[]>({
        route: ApiRoutes.GetTopSelling,
      });
      return (response.data || []) as IProduct[];
    } catch (error) {
      console.error("Error fetching all products:", error);
      throw error;
    }
  },

  fetchProductsByCollection: async (pageNumber: number = 0, collections: string): Promise<IProduct[]> => {
    try {
      const response = await HttpClient.get<IProduct[]>({
        route: ApiRoutes.GetProductsByCollection,
        uriParams: { pageNumber, collections }
      });
      return (response.data || []) as IProduct[];
    } catch (error) {
      console.error("Error fetching all products:", error);
      throw error;
    }
  },



  fetchProductById: async (productIds: string[]): Promise<IProduct[]> => {
    try {
      const response = await HttpClient.get<IProduct[]>({
        route: ApiRoutes.GetProductsById,
        uriParams: { productIds }
      });
      return (response.data || []) as IProduct[];
    } catch (error) {
      console.error("Error fetching all products:", error);
      throw error;
    }
  },

  // Fetch a single product by ID
  fetchCartDetails: async (): Promise<ICart> => {
    try {

      let response = await HttpClient.get<ICart>({
        route: ApiRoutes.GetCartItems
      });
      return response.data as ICart;
    } catch (error) {
      console.error(`Error fetching cart details:`, error);
      throw error;
    }
  },

  addToCart: async (addCartItem: IAddCartItem): Promise<IAddCartItem> => {
    try {
      const response = await HttpClient.post<IAddCartItem, IAddCartItem>({
        route: ApiRoutes.AddToCart,
        //uriParams:{addCartItem},
        body: addCartItem, // Send the object in the body
      });
      console.log("Item added to cart successfully:", response.data);
      if (!response.status) {
        response.message && SnackyPop.showSnack({ message: response.message });
        throw response;
      }
      let data = response.data as IAddCartItem;
      return data;
    } catch (error: any) {
      console.error("Error adding item to cart:", error);
      throw error;
    }
  },

  mergeCartOnLogin: async (): Promise<ICart> => {
    try {
      const guestCartItems = JSON.parse(localStorage.getItem("cart") || "[]") as ICartItem[];

      if (!Array.isArray(guestCartItems)) {
        console.error("Guest cart is not an array", guestCartItems);
        throw new Error("Invalid guest cart format");
      }

      // Construct a valid ICart object with default values
      const guestCart: ICart = {
        id:"",
        userId: "",  // No userId for guest users
        status: CartStatus.Active, // Assume active cart
        totalPrice: 0,
        finalPrice: 0,
        discount: 0,
        shippingCharge: 0,
        cartItems: guestCartItems,
      };

      const response = await HttpClient.post({
        route: ApiRoutes.MergeCart,
        body: guestCartItems, // Wrapping cartItems inside an object
      });
  
      if (!response.status) {
        response.message && SnackyPop.showSnack({ message: "Unable to add items in cart, please contact admin." });
        throw response;
      }
  
      console.log("Guest cart merged successfully:", response.data);
  
      // Clear guest cart after successful merge
      localStorage.removeItem("guestCart");
  
      return response.data as ICart;
    } catch (error: any) {
      console.error("Error merging guest cart:", error);
      throw error;
    }
  },
  

  removeFromCart: async (cartId: string): Promise<ICart> => {
    try {
      const response = await HttpClient.delete<ICart, ICart>({
        route: ApiRoutes.RemoveFromCart,
        //uriParams:{addCartItem},
        uriParams: { itemId: cartId }
      });
      console.log("Item added to cart successfully:", response.data);
      let data = response.data as ICart;
      return data;
    } catch (error) {
      console.error("Error adding item to cart:", error);
      throw error;
    }
  },

  async updateCartQuantity(cartItemId: string, quantity: number) {
    try {
      // Replace with actual API endpoint to update the cart item quantity
      const response = await HttpClient.post({
        route: ApiRoutes.UpdateCartQuantity,
        uriParams: { cartItemId: cartItemId, quantity: quantity }
      });
      // if (!response.ok) {
      //   throw new Error('Failed to update quantity');
      // }
      return true;//await response.json();
    } catch (error) {
      console.error("Error updating cart item quantity:", error);
      throw error;
    }
  },

  getStockAvailability: async(cartDetails:any): Promise<ICart> => {
    const response = await HttpClient.post({
      route: ApiRoutes.GetStockAvailability,
      body: cartDetails
    });
    return response.data as ICart;
  },
  // Checkout
  // checkout: async (cartItems: { productId: number; quantity: number }[]) => {
  //   try {
  //     const response = await api.post("/checkout", { items: cartItems });
  //     return response.data;
  //   } catch (error) {
  //     console.error("Error during checkout:", error);
  //     throw error;
  //   }
  // },
};

export default ProductService;
